import { lazy, Suspense, useEffect, useState } from 'react';
import { LANDING_COPY as copy } from './copy.ts';
import { DemoPhone } from './DemoPhone.tsx';
import './landing.css';

/**
 * @fileoverview The site's landing page: `mapshare.fyi/`, and the root of every
 * environment (2026-09-25, OQ-48).
 *
 * For people who have not seen MapShare: what it is, a demo they can touch
 * (a fictional organization, `DemoPhone`), and a sign-in that takes members to
 * their own organization — or, for someone with none, to creating one
 * (2026-09-28, OQ-51; `?account=new` opens straight to that form).
 *
 * **Light on first paint.** This file and what it imports statically are the
 * whole of the entry chunk (`main.tsx`): text, CSS and the demo's list. The
 * demo's map and the sign-in (Clerk) are chunks of their own, fetched when the
 * demo is on screen and when someone presses 「サインイン」.
 */

const SiteSignIn = lazy(() => import('./SiteSignIn.tsx'));

/** Whether the page was opened to finish a sign-in (see `SiteSignIn`). */
function returningToAccount(location: Location): boolean {
  // `#/…` is `<SignIn routing="hash">` in the middle of a flow.
  return new URLSearchParams(location.search).has('account') || location.hash.startsWith('#/');
}

/** `?account=new`: open the dialog to create an organization (`OrganizationPicker`). */
function creatingOrganization(location: Location): boolean {
  return new URLSearchParams(location.search).get('account') === 'new';
}

function AccountDialog({ onClose, create }: { onClose: () => void; create: boolean }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ms-dialog-backdrop" onClick={onClose}>
      <div
        className="ms-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={copy.account.dialogLabel}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="ms-dialog-close" onClick={onClose}>
          {copy.account.close}
        </button>
        <Suspense fallback={<p role="status">{copy.account.loading}</p>}>
          <SiteSignIn create={create} />
        </Suspense>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [accountOpen, setAccountOpen] = useState(() => returningToAccount(window.location));
  // Read before the effect below takes `?account` off the URL.
  const [create] = useState(() => creatingOrganization(window.location));

  useEffect(() => {
    // `?account` has done its job; a reload should not reopen the dialog.
    const url = new URL(window.location.href);
    if (!url.searchParams.has('account')) return;
    url.searchParams.delete('account');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const signIn = (
    <button type="button" className="ms-button" onClick={() => setAccountOpen(true)}>
      {copy.signIn}
    </button>
  );

  return (
    <div className="ms-landing">
      <header className="ms-bar">
        <span className="ms-logo">
          Map<span>Share</span>
        </span>
        {signIn}
      </header>

      <main>
        <section className="ms-hero">
          <div>
            <h1>
              {copy.hero.title.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </h1>
            {copy.hero.lead.map((line) => (
              <p key={line} className="ms-lead">
                {line}
              </p>
            ))}
            <div className="ms-hero-actions">{signIn}</div>
            <p className="ms-note">{copy.hero.notice}</p>
          </div>
          <figure className="ms-demo-figure">
            <DemoPhone />
            <figcaption className="ms-note">{copy.demo.caption}</figcaption>
          </figure>
        </section>

        <section className="ms-band" aria-labelledby="ms-values">
          <h2 id="ms-values">{copy.values.heading}</h2>
          <div className="ms-grid">
            {copy.values.items.map((item, index) => (
              <div key={item.title} className="ms-card">
                <div className="ms-card-number">{String(index + 1).padStart(2, '0')}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="ms-band" aria-labelledby="ms-steps">
          <h2 id="ms-steps">{copy.steps.heading}</h2>
          <ol className="ms-steps">
            {copy.steps.items.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <p>{item.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="ms-band" aria-labelledby="ms-status">
          <div className="ms-status">
            <h2 id="ms-status">{copy.status.heading}</h2>
            {copy.status.body.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {signIn}
          </div>
        </section>
      </main>

      <footer className="ms-footer">
        <p>{copy.footer.fictional}</p>
        <p>© {copy.brand}</p>
      </footer>

      {accountOpen && <AccountDialog onClose={() => setAccountOpen(false)} create={create} />}
    </div>
  );
}
